import { useState } from 'react';
import { ApiError } from '../lib/api';
import { stopImpersonating } from '../lib/impersonation';

// Persistent bar shown app-wide while an admin is "viewing as" another user.
// It is the single, always-visible exit from impersonation (the rest of the
// app faithfully renders as the target, including hiding admin-only chrome).

export interface ImpersonationBannerProps {
  /** The real admin behind the curtain. */
  actorEmail: string;
  /** The user being emulated. */
  viewingEmail: string;
  /** Human label for the target's access (e.g. "Sales rep"). */
  roleLabel: string;
}

export default function ImpersonationBanner({
  actorEmail,
  viewingEmail,
  roleLabel,
}: ImpersonationBannerProps) {
  const [exiting, setExiting] = useState(false);

  async function exit() {
    setExiting(true);
    try {
      await stopImpersonating();
    } catch (err) {
      setExiting(false);
      window.alert(err instanceof ApiError ? err.message : 'Could not exit. Try again.');
    }
  }

  return (
    <div className="impersonation-banner" role="status" aria-live="polite">
      <span className="impersonation-banner__eye" aria-hidden="true">👁</span>
      <span className="impersonation-banner__txt">
        Viewing as <strong>{viewingEmail}</strong>
        <span className="impersonation-banner__role"> · {roleLabel}</span>
        <span className="impersonation-banner__actor"> — read-only, as {actorEmail}</span>
      </span>
      <button
        type="button"
        className="impersonation-banner__exit"
        onClick={exit}
        disabled={exiting}
      >
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  );
}
